/**
 * Qual proposta ABERTA este cartão responde.
 *
 * ★ [#215] Medido em produção (19/09/2026, escritório 83, run
 * `25c0eddc-8eff-42f2-b075-ec3dae242d50`): o cartão de escrita só é desenhado
 * DEPOIS do `end`, pela bolha PERSISTIDA ("A ordem do turno é UMA") — e a
 * `tool_call` que o backend persiste traz exatamente
 * `id, name, input, executor, state, output, ui, label, erro`.
 *
 * Não há `id_proposta` ali. A parte VIVA do slice tem (o
 * `_onToolApprovalRequest` a copia), mas ela é justamente a que a bolha em
 * streaming não desenha — quando o cartão aparece, a parte que o alimenta já é
 * a persistida. Resultado: `idDaPropostaDoCartao` devolvia `null`, o guard caía
 * em `!chatLoading` e o clique virava a FRASE de protocolo em vez da decisão —
 * o mesmo desfecho que o [#214] fechou pela outra porta (quatro propostas
 * `aberta` para o mesmo cenário e zero linhas em `partners_fee_fixo_analise`).
 *
 * O casamento por `tool` é o fallback, e é DETERMINÍSTICO de propósito: só vale
 * com UMA candidata. Duas propostas abertas da mesma tool são ambíguas, e
 * decidir a errada grava a carteira de um cenário no lugar de outro — pior que
 * não decidir. Ambiguidade devolve `null` e o caminho antigo segue.
 *
 * ⚠️ Isto NÃO substitui a correção de raiz: o backend deveria persistir
 * `id_proposta` no `tool_calls[]` (e o `ui`, hoje nulo na cópia). Enquanto não
 * persistir, este é o único elo que liga o cartão à proposta que ele decide.
 */
import { propostaEstaAberta } from '@modules/aawz-intelligence-v2/utils/proposta-aberta'

// O estado em que a parte AINDA pede decisão. Fora dele não há o que decidir:
// um cartão já resolvido casando por tool reabriria uma escrita concluída.
const ESTADO_APROVACAO = 'approval-requested'

/** A tool que produziu a parte — `tool` no turno vivo, `name` na persistida. */
function toolDaParte(parte) {
  if (!parte) return ''
  return String(parte.tool || parte.name || '')
}

/**
 * @param {Object=} args
 * @param {Object=} args.parte            A parte RESOLVIDA que o cartão carrega.
 * @param {Object=} args.propostasAbertas O mapa do store, por `id_proposta`.
 * @returns {?string} O `id_proposta` decidível, ou `null`.
 */
export function idDaPropostaParaOCartao({ parte, propostasAbertas } = {}) {
  const mapa = propostasAbertas || {}
  // ⚠️ DUAS grafias: a parte CRUA do store guarda `id_proposta`; a RESOLVIDA
  // (`renderiza-partes.js::montarCartao`) a reescreve como `idProposta`.
  const explicito = parte && (parte.idProposta || parte.id_proposta)
  if (explicito) return mapa[explicito] ? explicito : null

  if (!parte || parte.state !== ESTADO_APROVACAO) return null
  const tool = toolDaParte(parte)
  if (!tool) return null

  const candidatas = Object.keys(mapa).filter(function(id) {
    const p = mapa[id]
    return Boolean(p) && propostaEstaAberta(p) && Boolean(p.preview) && p.preview.tool === tool
  })
  return candidatas.length === 1 ? candidatas[0] : null
}

export default { idDaPropostaParaOCartao }
